/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PreferencesUpdateContactInformation.scss';

import { array, object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isEqual, omit } from 'lodash';

import Store from 'state/Store';
import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning-circle.svg';
import AddressValidationModal from 'components/layouts/Preferences/UpdateContactInformation/AddressValidation/AddressValidationModal';
import AccountSelectionModal from 'components/layouts/Preferences/UpdateContactInformation/AccountSelectionModal';
import EmailPhone from 'components/layouts/Preferences/UpdateContactInformation/EmailPhone/EmailPhone';
import MailingAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/MailingAddress';
import PersonalInformation from 'components/layouts/Preferences/UpdateContactInformation/PersonalInformation';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import {
  appAddressToComponentAddressMapper,
  appEmailToComponentEmailMapper,
  appPhoneNumberToComponentPhoneNumberMapper,
  componentAddressToAppAddressMapper,
  componentEmailToAppEmailMapper,
  componentPhoneNumberToAppPhoneNumberMapper,
  MAILING_ADDRESS
} from 'services/Preferences';
import {
  updateCustomerPreferences,
  updateCustomerPreferencesV2
} from 'services/Rest';
import {
  getPrimaryEmailAddress,
  getPrimaryMailingAddress,
  getPhoneNumbers,
  hasResidentialAddress,
  getCustomerContactInformation,
  getResidentialAddress
} from 'state/customer/CustomerSelectors';
import { getMilitaryStates } from 'state/api/MetadataSelectors';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import {
  errorUpdateContactInformation,
  hideFixedFooterSaveOptions,
  showFixedFooterSaveOptions,
  
} from 'state/preferences/PreferencesActions';
import { isSalesforceRepEnabled } from 'state/view/ViewSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import ResidentialAddress from './ResidentialAddress/ResidentialAddress';
import { shouldDisableSave } from 'support/AddressUtils';
import { clearInvalidFields, clearTouchedFields } from 'state/validation/ValidationActions';
import { residentialAddressEnabled } from 'services/FeatureFlags';
import {
  has529Only,
  hasOwnedBrokerage
} from "state/account/AccountSelectors";
import Content from "services/Content";

export const getWarningTextContent = (addressType, owns529Only, ownsBrokerage) => {
  if(residentialAddressEnabled()) {
    if(addressType === 'non-us') {
      if(owns529Only)
        return Content.nonUsAddressInfo529Only;
      else if (ownsBrokerage)
        return Content.nonUsAddressInfoBrokerage;
      else
        return Content.residentialAddressnonUsAddressInfo;
    }
    else {
      if(owns529Only)
        return Content.usAddressInfo529Only
      else
        return Content.residentialAddressusAddressInfo;
    }
  }
  else {
    if(addressType === 'non-us')
      return Content.nonUsAddressInfo;
    else
      return Content.usAddressInfo;
  }
}
class PreferencesUpdateContactInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAccountSelectionModal: false,
      showAddressValidationModal: false,
      updatedContactInformation: {},
      sameAsResidentialAddress: false,
      ...this.copyBffState()
    };
  }

  componentDidMount() {
    // This form uses FormattedInput components which triggers onChange()
    // on data load. Therefore we force everything to be hidden after
    // all data is loaded.
    Store.dispatch(hideFixedFooterSaveOptions());
  }

  componentDidUpdate(prevProps) {
    const { requestStatus } = this.props;
    const receivedCustomerPreferences =
      requestStatus.customerPreferences === REQUEST_STATES.received;
    const previouslyRequestedCustomerPreferences =
      prevProps.requestStatus.customerPreferences !== REQUEST_STATES.received;

    //Below needs to be called on retrieval of customerPreferences and when updating contact information to ensure integrity of component state.
    if (receivedCustomerPreferences && previouslyRequestedCustomerPreferences) {
      const newBffState = this.copyBffState();
      this.setState(newBffState);
    }
  }

  copyBffState() {
    const {
      militaryStates,
      primaryEmailAddress,
      primaryMailingAddress,
      phoneNumbers,
      residentialAddress,
      hasResidential
    } = this.props;

    const addressState = hasResidential ? residentialAddress.state : primaryMailingAddress.state;

    const isMilitary = militaryStates.some(
      (state) => state.code === addressState
    );

    const addressData = {
      address: appAddressToComponentAddressMapper(
        hasResidential ? residentialAddress : primaryMailingAddress,
        isMilitary
      )
    };

    if (hasResidential) {
      const isMailingAddressMilitary =
        primaryMailingAddress &&
        militaryStates.some(
          (state) => state.code === primaryMailingAddress.state
        );
      addressData.mailingAddress = appAddressToComponentAddressMapper(
        primaryMailingAddress,
        isMailingAddressMilitary
      );
    }

    return {
      ...addressData,
      sameAsResidentialAddress:
        (primaryMailingAddress && primaryMailingAddress.sameAsResidential) ||
        false,
      email: appEmailToComponentEmailMapper(primaryEmailAddress),
      phoneNumbers: appPhoneNumberToComponentPhoneNumberMapper(phoneNumbers)
    };
  }

  updateStateAfterSave = () => {
    const newBffState = this.copyBffState();
    this.setState(newBffState);
  };

  updateMailingAddressAsResidential = (checkboxValue) => {
    const { address } = this.state;
    const {
      primaryMailingAddress,
      militaryStates
    } = this.props;

    Store.dispatch(clearInvalidFields());
    Store.dispatch(clearTouchedFields());

    Store.dispatch(showFixedFooterSaveOptions());

    let sameAddress;

    if (checkboxValue) {
      sameAddress = {
        sameAsResidential: checkboxValue,
        ...address
      };
    } else {
      const isMailingAddressMilitary =
        primaryMailingAddress &&
        militaryStates.some(
          (state) => state.code === primaryMailingAddress.state
        );
      sameAddress = {
        sameAsResidential: checkboxValue,
        ...appAddressToComponentAddressMapper(
          primaryMailingAddress,
          isMailingAddressMilitary
        )
      };
    }

    this.setState({
      mailingAddress: sameAddress,
      sameAsResidentialAddress: checkboxValue
    });
  };

  shouldValidateDisableSave = () => {
    const {
      invalidFields,
      hasResidential,
      primaryMailingAddress,
      residentialAddress,
      militaryStates,
      primaryEmailAddress,
      phoneNumbers : initialPhoneNumbers
    } = this.props;
    const { email, address, mailingAddress, phoneNumbers : updatedPhoneNumbers } = this.state;

    const isMailingMilitary = militaryStates.some(
      (state) =>
        state.code === primaryMailingAddress.state
    );

    const isResidentialMilitary = militaryStates.some(
      (state) => state.code === residentialAddress.state
    );
    
    const updatedMailingAddress = appAddressToComponentAddressMapper(
      primaryMailingAddress,
      isMailingMilitary
    );
    const updatedResidentialAddress = hasResidential
      ? appAddressToComponentAddressMapper(
          residentialAddress,
          isResidentialMilitary
        )
      : {};

    return shouldDisableSave(
      invalidFields,
      hasResidential,
      email,
      updatedPhoneNumbers,
      address,
      mailingAddress,
      updatedMailingAddress,
      updatedResidentialAddress,
      primaryEmailAddress,
      initialPhoneNumbers
      );
  };

  handleCancel = () => {
    const { history } = this.props;
    history.push('/profile');
  };

  handleSavePreferences = () => {
    const {
      address,
      mailingAddress,
      email,
      phoneNumbers,
      sameAsResidentialAddress
    } = this.state;

    const { hasResidential } = this.props;
    const isForeign = address.addressType === 'non-us';
    const appEmail = componentEmailToAppEmailMapper(
      email,
      this.props.primaryEmailAddress
    );
    const appPhoneNumbers = componentPhoneNumberToAppPhoneNumberMapper(
      phoneNumbers,
      this.props.phoneNumbers,
      isForeign
    );

    const addressData = {};

    if (hasResidential) {
      addressData.residentialAddress = componentAddressToAppAddressMapper(
        address,
        this.props.residentialAddress
      );

      if(sameAsResidentialAddress && addressData.residentialAddress.disposition) {
        addressData.primaryMailingAddress = componentAddressToAppAddressMapper(
          address,
          this.props.residentialAddress
        );
        addressData.primaryMailingAddress = {
          ...addressData.primaryMailingAddress,
          disposition: {
            action: 'update'
          }
        }
      }else{
        addressData.primaryMailingAddress = componentAddressToAppAddressMapper(
          mailingAddress,
          this.props.primaryMailingAddress
        );
      }
    } else {
      addressData.primaryMailingAddress = componentAddressToAppAddressMapper(
        address,
        this.props.primaryMailingAddress
      );
    }

    const contactInformation = {
      ...addressData,
      primaryMailingAddress: {
        ...addressData.primaryMailingAddress,
        typeId: MAILING_ADDRESS,
        sameAsResidential: sameAsResidentialAddress
      },
      primaryEmailAddress: appEmail,
      phoneNumbers: appPhoneNumbers
    };

    if(hasResidential) {
      if (addressData.residentialAddress.disposition || addressData.primaryMailingAddress.disposition) {
        // If the address has changed, we do address validation for us-based addresses and military addresses and then account selection.
        // For non-us addresses we go straight to account selection
        if(addressData.residentialAddress.foreign && addressData.primaryMailingAddress.foreign) {
          this.setState({
            showAccountSelectionModal: true,
            updatedContactInformation: contactInformation
          });
        } else {
          if ((address.addressType === 'us' || address.addressType === 'military') || (mailingAddress.addressType === 'us' || mailingAddress.addressType === 'military')) {
            this.setState({
              showAddressValidationModal: true,
              updatedContactInformation: contactInformation
            });
          }
        }
      } else {
        const preferences = {
          contactInformation
        };
        if(residentialAddressEnabled()) {
          updateCustomerPreferencesV2(
            preferences,
            () => this.updateStateAfterSave(),
            () => {
              Store.dispatch(errorUpdateContactInformation())
            }
          );
        } else {
          updateCustomerPreferences(
            preferences,
            () => this.updateStateAfterSave(),
            () => {
              Store.dispatch(errorUpdateContactInformation());
            }
          );
        }
      }
    } else {
      if (addressData.primaryMailingAddress.disposition) {
        // If the address has changed, we do address validation for us-based addresses and military addresses and then account selection.
        // For non-us addresses, we go straight to account selection
        if (address.addressType === 'us' || address.addressType === 'military') {
          this.setState({
            showAddressValidationModal: true,
            updatedContactInformation: contactInformation
          });
        } else {
          this.setState({
            showAccountSelectionModal: true,
            updatedContactInformation: contactInformation
          });
        }
      } else {
        const preferences = {
          contactInformation
        };

        updateCustomerPreferences(
          preferences,
          () => this.updateStateAfterSave(),
          () => {
            Store.dispatch(errorUpdateContactInformation());
          }
        );

      }
    }
  };

  onChange = (parentKey, childKey) => (newValue) => {
    const childState = this.state[parentKey];
    const currentChildStateForKey = childState[childKey];
    if (isEqual(currentChildStateForKey, newValue)) {
      return;
    }

    const newChildState = {
      ...childState,
      [childKey]: newValue
    };

    this.setState({ [parentKey]: newChildState });

    Store.dispatch(showFixedFooterSaveOptions());
  };

  handleAddressValidationSelect = (contactInformation) => {
    this.setState({
      showAccountSelectionModal: true,
      showAddressValidationModal: false,
      updatedContactInformation: contactInformation
    });
  };

  handleUpdatedContactInformationChange = (contactInformation) => {
    this.setState({ updatedContactInformation: contactInformation });
  };

  handleAccountSelect = (accountDescriptors) => {
    const { hasResidential } = this.props;
    const { updatedContactInformation } = this.state;
    let preferences;
    if(hasResidential) {
      const { primaryMailingAddress, residentialAddress } = updatedContactInformation;
      const mappedResidentialAddress = omit(residentialAddress, 'sameAsResidential');
      preferences = {
        contactInformation: {
          ...updatedContactInformation,
          primaryMailingAddress: {
            ...primaryMailingAddress,
            accountDescriptors
          },
          residentialAddress: {
            ...mappedResidentialAddress,
            accountDescriptors
          }
        }
      };
    } else {
      const { primaryMailingAddress } = updatedContactInformation;
      preferences = {
        contactInformation: {
          ...updatedContactInformation,
          primaryMailingAddress: {
            ...primaryMailingAddress,
            accountDescriptors
          }
        }
      };
    }

    if(residentialAddressEnabled()) {
      updateCustomerPreferencesV2(
        preferences,
        () => this.updateStateAfterSave(),
        () => {
          Store.dispatch(errorUpdateContactInformation());
        }
      );
    } else {
      updateCustomerPreferences(
        preferences,
        () => this.updateStateAfterSave(),
        () => {
          Store.dispatch(errorUpdateContactInformation());
        }
      );
    }

    this.setState({ showAccountSelectionModal: false });
  };

  handleAccountSelectionClose = () =>
    this.setState({ showAccountSelectionModal: false });

  handleAddressValidationClose = () =>
    this.setState({ showAddressValidationModal: false });

  render() {
    const {
      address,
      mailingAddress,
      email,
      phoneNumbers,
      showAccountSelectionModal,
      showAddressValidationModal,
      updatedContactInformation,
      sameAsResidentialAddress
    } = this.state;

    const {
      isDesktopLayout,
      contactInfoWasSaved,
      hideFixedFooterSaveOptions,
      hasResidential,
      owns529Only,
      ownsBrokerage
    } = this.props;

    const updatedMailingAddress = hasResidential ? mailingAddress : address;

    return (
      <Fragment>
        {showAddressValidationModal && (
          <AddressValidationModal
            enteredAddress={
              hasResidential
                ? updatedContactInformation.residentialAddress
                : updatedContactInformation.primaryMailingAddress
            }
            updatedContactInformation={updatedContactInformation}
            handleUpdatedContactInformationChange={this.handleUpdatedContactInformationChange}
            onSelect={this.handleAddressValidationSelect}
            onClose={this.handleAddressValidationClose}
            hasResidential={hasResidential}
          />
        )}
        {showAccountSelectionModal && (
          <AccountSelectionModal
            onSelect={this.handleAccountSelect}
            onClose={this.handleAccountSelectionClose}
          />
        )}
        <PreferencesHeader>
          <div className="update-contact-information-header">
            Update Contact Information
          </div>
        </PreferencesHeader>
        <PreferencesSection className="update-contact-information">
          <PersonalInformation />
          {hasResidential && (
            <ResidentialAddress
              address={address}
              onChange={this.onChange}
              hasResidentialAddress={hasResidential}
              owns529Only={owns529Only}
              ownsBrokerage={ownsBrokerage}
              mailingAddress={updatedMailingAddress}
            />
          )}
          <MailingAddress
            address={updatedMailingAddress}
            onChange={this.onChange}
            hasResidentialAddress={hasResidential}
            isMailingSameAsResidential={sameAsResidentialAddress}
            onSameAsSelected={this.updateMailingAddressAsResidential}
            owns529Only={owns529Only}
            ownsBrokerage={ownsBrokerage}
          />
          <EmailPhone
            addressType={address.addressType}
            email={email}
            onChange={this.onChange}
            phoneNumbers={phoneNumbers}
          />
          <PreferencesRow className="update-contact-information preferences-buttons">
            <PreferencesCol col={9} />
            <PreferencesCol col={3}>
              {contactInfoWasSaved && isDesktopLayout && (
                <div className="disclosure-warning">
                  <WarningCircleIcon className="disclosure-warning-icon" />
                  <span className="disclosure-warning-text">
                    Your changes have been saved. It may take up to 24 hours for
                    the changes to reflect on your profile page.
                  </span>
                </div>
              )}
            </PreferencesCol>
          </PreferencesRow>
          <FixedSubmitFooter
            cancel={this.handleCancel}
            className="primary-button save-button"
            submit={this.handleSavePreferences}
            requestStatusKey="updateCustomerPreferences"
            text="Save"
            shouldDisableSubmit={this.shouldValidateDisableSave()}
            shouldShow={!hideFixedFooterSaveOptions}
          />
        </PreferencesSection>
      </Fragment>
    );
  }
}

PreferencesUpdateContactInformation.propTypes = {
  invalidFields: object.isRequired,
  militaryStates: array.isRequired,
  primaryEmailAddress: object.isRequired,
  primaryMailingAddress: object.isRequired,
  phoneNumbers: array.isRequired,
  touchedFields: object.isRequired
};

const mapStateToProps = (state) => ({
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  invalidFields: state.invalidFields,
  militaryStates: getMilitaryStates(state),
  primaryEmailAddress: getPrimaryEmailAddress(state),
  primaryMailingAddress: getPrimaryMailingAddress(state),
  phoneNumbers: getPhoneNumbers(state),
  touchedFields: state.touchedFields,
  isDesktopLayout: state.view.isDesktopLayout,
  contactInfoWasSaved: state.contactInfoWasSaved,
  router: state.router,
  hideFixedFooterSaveOptions: state.hideFixedFooterSaveOptions,
  requestStatus: getRequestStatus(state),
  hasResidential: hasResidentialAddress(state),
  residentialAddress: getResidentialAddress(state),
  contactInfo: getCustomerContactInformation(state),
  owns529Only: has529Only(state),
  ownsBrokerage: hasOwnedBrokerage(state)
});

export default withRouter(
  connect(mapStateToProps)(PreferencesUpdateContactInformation)
);
